import { RiArrowDownSLine } from '@remixicon/react';
import { Dialog, DialogTrigger, Popover } from 'react-aria-components';
import { AgButton } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';

type ToolbarButtonProps = {
  icon?: React.ReactNode;
  label?: string;
  /**
   * Accessible name for the ICON-ONLY variant, which renders no visible text.
   * The labelled variant is named by `label` itself.
   */
  ariaLabel?: string;
  children: (closeMenu: () => void) => React.ReactNode;
  'data-testid'?: string;
};

/** Every icon-only ToolbarButton on the platform is the gear that opens the column panel. */
const ICON_ONLY_DEFAULT_LABEL = 'Customize table';

export default function ToolbarButton(props: ToolbarButtonProps) {
  const isIconOnly = props.icon && !props.label;
  const dataTestId = props['data-testid'];
  // The trigger and the dialog it opens carry the SAME name — the button says what opens.
  const accessibleName = isIconOnly ? (props.ariaLabel ?? ICON_ONLY_DEFAULT_LABEL) : props.label;

  return (
    <DialogTrigger>
      {isIconOnly ? (
        <AgButton
          variant="white"
          size="icon-sm"
          shape="rounded"
          className="my-auto"
          aria-label={accessibleName}
          data-testid={dataTestId}
        >
          {props.icon}
        </AgButton>
      ) : (
        <AgButton variant="white" shape="rounded" className="my-auto gap-1" data-testid={dataTestId}>
          {props.label}
          <RiArrowDownSLine size={16} />
        </AgButton>
      )}
      <Popover
        placement="bottom end"
        maxHeight={500}
        className={cn(
          'min-w-[var(--trigger-width)] rounded-md bg-white border border-neutral-5 shadow-md outline-none',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <Dialog aria-label={accessibleName} className="outline-none max-h-[inherit] flex flex-col overflow-hidden">
          {({ close }) => props.children(close)}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
